import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MeasurementSilhouette } from "@/components/measurements/MeasurementSilhouette";
import type { MeasurementData } from "@/lib/types/measurements";
import { AlertCircle } from "lucide-react";

const sampleMeasurement: MeasurementData = {
  bust: 92,
  waist: 76,
  hip: 98,
  shoulder: 38,
  sleeveLength: 58,
  dressLength: 102,
  unit: "cm",
};

export default function MeasurementsDemoPage() {
  return (
    <div className="container py-12 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Measurement Viewer Demo</h1>
        <p className="text-muted-foreground">
          Interactive visualization of customer measurements
        </p>
      </div>

      <div className="grid gap-6 mb-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Customer ID: HM-0001</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Sample measurement data</p>
              </div>
              <Badge className="bg-green-100 text-green-800 border-green-200">
                Accepted
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-2 p-3 bg-accent rounded-lg">
              <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5" />
              <p className="text-sm text-muted-foreground">
                This is a demo page showing the interactive measurement viewer component.
                In production, this will display actual customer measurements with version history.
                Edit functionality is not available in demo mode.
              </p>
            </div>
          </CardContent>
        </Card>

        <MeasurementSilhouette data={sampleMeasurement} />

        <Card>
          <CardHeader>
            <CardTitle>About This Component</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              The Visual Measurements Viewer provides an intuitive way to view and understand
              customer measurements through an interactive SVG silhouette.
            </p>
            <div className="space-y-2">
              <p className="font-semibold text-foreground">Features:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Interactive hover and click on measurement lines</li>
                <li>Unit conversion between centimeters and inches</li>
                <li>Visual highlighting of selected measurements</li>
                <li>Side panel with all measurements for easy reference</li>
                <li>Responsive design that works on all screen sizes</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-foreground">Future Enhancements:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Measurement version history and comparison</li>
                <li>Edit mode for staff to update measurements</li>
                <li>Photo overlay for reference</li>
                <li>Measurement validation and warnings</li>
                <li>Export measurements as PDF</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
